import React from "react";
import { Link } from "react-router-dom";
import {
  Scale,
  ArrowLeft,
  Mail,
  KeyRound,
  CreditCard,
  Wallet,
  LifeBuoy,
  ShieldCheck,
  MessageSquare,
  ArrowRight,
} from "lucide-react";
import { LandingFooter } from "@/components/landing/CtaBanner";

// The only contact address currently configured for the service (ADMIN_EMAIL).
// If a dedicated support address is set up later, change this one constant.
const CONTACT_EMAIL = "supravat111@gmail.com";

const TOPICS = [
  {
    id: "account",
    icon: KeyRound,
    title: "Account or login issues",
    body:
      "Can't sign in, forgot password not working, locked out after too many attempts, wrong role on your account, or you can't see your firm's data after signing in.",
    subject: "Advocate Diary — Account or login issue",
    hint: "Include your account email and, if you have it, the exact error message you see on screen.",
  },
  {
    id: "subscription",
    icon: CreditCard,
    title: "Subscription and billing questions",
    body:
      "Choosing between Pro Advocate and Law Firm, moving from monthly to yearly, downgrading, cancelling from Settings, or a question about the plan currently on your account.",
    subject: "Advocate Diary — Subscription / billing question",
    hint: "Tell us the plan you are on (or the plan you want to move to) and the account email.",
  },
  {
    id: "payment",
    icon: Wallet,
    title: "Payment issues",
    body:
      "A payment did not go through, you were charged but the plan is not active, you see a duplicate charge, or you need help with a refund or billing correction.",
    subject: "Advocate Diary — Payment / refund issue",
    hint:
      "Include the account email, the plan attempted, the approximate date and amount, and any Razorpay payment or order reference from the receipt or your bank statement.",
  },
  {
    id: "technical",
    icon: LifeBuoy,
    title: "Technical problems",
    body:
      "A page won't load, a document upload or download fails, hearings or reminders are missing, an export is broken, or something is not behaving the way the app suggests it should.",
    subject: "Advocate Diary — Technical problem",
    hint:
      "Tell us what you were trying to do, what happened instead, and — if you can — the device (desktop or phone), browser and time it happened. A screenshot helps.",
  },
  {
    id: "privacy",
    icon: ShieldCheck,
    title: "Privacy or data requests",
    body:
      "Access to the information stored on your account, correction of your account details, or removal of your account and firm data. See the Privacy Policy for what is stored and how these requests are handled.",
    subject: "Advocate Diary — Privacy / data request",
    hint:
      "Please write from the email address registered on the account so we can be reasonably satisfied the request comes from you.",
  },
  {
    id: "general",
    icon: MessageSquare,
    title: "General product support",
    body:
      "You are new to Advocate Diary and want a pointer, or you have a question about how a specific feature is meant to be used, or feedback on how it could work better.",
    subject: "Advocate Diary — General question",
    hint: "Tell us what you are trying to achieve — that is usually the fastest route to a useful answer.",
  },
];

const RELATED = [
  ["Pricing", "/pricing", "route"],
  ["Privacy Policy", "/privacy-policy", "route"],
  ["Terms of Service", "/terms", "route"],
  ["Cancellation & Refund Policy", "/refund-policy", "route"],
];

const mailto = (subject) =>
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

export default function Contact() {
  return (
    <div data-testid="contact-page" className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="contact-brand" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
              <Scale className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <span className="font-display text-xl tracking-tight text-navy">Advocate Diary</span>
          </Link>
          <Link
            to="/"
            data-testid="contact-back-home"
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm text-navy/70 transition-colors hover:bg-secondary hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </Link>
        </div>
      </header>

      <div className="border-b border-border bg-navy px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold">Support</p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Contact &amp; Support
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            Get in touch with the Advocate Diary team. We reply from a person, not a helpdesk queue.
          </p>
          <a
            href={mailto("Advocate Diary — Hello")}
            data-testid="contact-hero-email"
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3 text-sm font-semibold text-navy shadow-lg transition-all hover:-translate-y-0.5 hover:bg-gold-light"
          >
            <Mail className="h-4 w-4" />
            {CONTACT_EMAIL}
          </a>
          <p className="mt-3 text-xs text-slate-400">
            Please write from the email address registered on your account.
          </p>
        </div>
      </div>

      <main className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">
              What can we help with?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">
              Pick the topic that best matches what you need. Each one opens a new email addressed to us with
              a matching subject line, so your message reaches the right place without you having to think
              about it.
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            {TOPICS.map((t) => {
              const Icon = t.icon;
              return (
                <a
                  key={t.id}
                  href={mailto(t.subject)}
                  data-testid={`contact-topic-${t.id}`}
                  className="group flex h-full flex-col rounded-lg border border-border bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-navy text-gold">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </span>
                    <div className="flex-1">
                      <h3 className="font-display text-lg font-semibold text-navy">{t.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
                    <span className="font-semibold text-navy/80">Helpful to include:</span> {t.hint}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy transition-colors group-hover:text-gold-dark">
                    Email us
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </a>
              );
            })}
          </div>

          <div className="mt-14 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
            <section
              data-testid="contact-how-we-work"
              className="rounded-lg border border-border bg-secondary/40 p-6 sm:p-8"
            >
              <h2 className="font-display text-2xl font-semibold tracking-tight text-navy sm:text-[1.7rem]">
                How support works
              </h2>
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">
                <p>
                  Advocate Diary is a small team building software for advocates and law firms. Support is
                  handled over email so that questions, screenshots and any account references stay in one
                  place we can act on. We aim to reply within a reasonable time; we do not publish a fixed
                  response-time guarantee, because that timing is not something we have committed to.
                </p>
                <p>
                  For anything that concerns your account or a payment, please write from the email address
                  registered on the account. This helps us be reasonably satisfied the request is coming from
                  you before we make changes on your behalf.
                </p>
                <p>
                  For refunds and billing corrections, please have a look at our{" "}
                  <Link
                    to="/refund-policy"
                    data-testid="contact-inline-refund"
                    className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                  >
                    Cancellation &amp; Refund Policy
                  </Link>{" "}
                  first. It sets out which situations are considered for a refund, what to include in the
                  request and how a refund is issued through Razorpay.
                </p>
                <p>
                  For questions about the information stored on your account, our{" "}
                  <Link
                    to="/privacy-policy"
                    data-testid="contact-inline-privacy"
                    className="font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                  >
                    Privacy Policy
                  </Link>{" "}
                  describes what is collected, how it is used and how to make a privacy request.
                </p>
              </div>
            </section>

            <aside data-testid="contact-related" className="rounded-lg border border-border bg-white p-6 sm:p-8">
              <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
                Related
              </p>
              <ul className="mt-4 space-y-3">
                {RELATED.map(([label, href, kind]) => (
                  <li key={label}>
                    {kind === "landing" ? (
                      <a
                        href={href}
                        data-testid={`contact-related-${label.toLowerCase().split(/\s+/)[0]}`}
                        className="group inline-flex items-center gap-1.5 text-sm font-medium text-navy transition-colors hover:text-gold-dark"
                      >
                        {label}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </a>
                    ) : (
                      <Link
                        to={href}
                        data-testid={`contact-related-${label.toLowerCase().split(/\s+/)[0]}`}
                        className="group inline-flex items-center gap-1.5 text-sm font-medium text-navy transition-colors hover:text-gold-dark"
                      >
                        {label}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mt-6 border-t border-border pt-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">Email</p>
                <a
                  href={mailto("Advocate Diary — Hello")}
                  data-testid="contact-sidebar-email"
                  className="mt-2 block break-all text-sm font-medium text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-dark"
                >
                  {CONTACT_EMAIL}
                </a>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                  No telephone number or postal address is published for support, and none should be assumed.
                  Email is the contact route for the service.
                </p>
              </div>
            </aside>
          </div>

          <div className="mt-14 rounded-lg border border-border bg-white p-6 sm:p-8" data-testid="contact-disclaimer">
            <h2 className="font-display text-xl font-semibold tracking-tight text-navy">
              A note on what we do
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-[0.95rem]">
              Advocate Diary is a software service for legal practice management — cases, hearings, clients,
              documents, tasks and a read-only client portal. It is not a law firm, does not act as an
              advocate for any person and <strong className="text-navy">does not provide legal advice</strong>.
              Using the application does not create an advocate-client relationship between you and Advocate
              Diary. For legal advice on a matter, please consult a qualified advocate.
            </p>
          </div>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
